import { CfoWorkbookDownloadButton, useCfoWorkbookDownload } from './CfoWorkbookDownload'
import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { AppWorkerClient } from '../../../data/AppWorkerClient'
import type {
  InsightDrillTarget,
  MetricComparisonKind,
  QuickBooksFpaWorkbookStatementBinding,
  ServerApiClient,
} from '../../../data/ServerApiClient'
import type { AccountingWorkbookBootstrap } from '../../../data/accountingCfoAdapters'
import { isDemoSession } from '../../../data/demoSession'
import { AnalyticsProvider } from '../../../analytics'
import { CfoHomeBrief } from '../CfoHomeBrief'
import { CfoCeoCockpitPage } from './ceo/CfoCeoCockpitPage'
import {
  cfoNavSectionsForProfile,
  resolveCfoPageFromDrillTarget,
  resolveCfoPageFromNavId,
  type CfoNavProfile,
  type CfoPageRoute,
} from './cfoNav'
import { CfoFinancialsPage } from './CfoFinancialsPage'
import { CfoClosePage } from './CfoClosePage'
import { CfoQoePage } from './CfoQoePage'
import { CfoJobCostingPage } from './CfoJobCostingPage'
import { CfoCashPage } from './CfoCashPage'
import { CfoRatiosPage } from './CfoRatiosPage'
import { CfoAnalysisProfilePage } from './CfoAnalysisProfilePage'
import { CfoInteriorPage } from './CfoInteriorPage'
import { CFO_SURFACES } from './cfoTelemetry'
import { mapServerClaim } from './cfoUnderstanding'
import { CfoArPage } from './CfoArPage'
import { CfoForecastPage } from './CfoForecastPage'
import { CfoScenariosPage } from './CfoScenariosPage'
import { CfoValuationPage } from './CfoValuationPage'
import { CfoPresentationsPage } from './CfoPresentationsPage'
import { CfoExplorePage, type CfoExploreSurface } from './CfoExplorePage'
import { CfoGlDetailExplorePage } from './CfoGlDetailExplorePage'
import { CfoAdvancedPage } from './CfoAdvancedPage'
import { CfoAnalysisProvider } from './analysis'
import { CfoPageLoading } from './CfoPageLoading'
import { CfoNavAvailabilityProvider, CfoShellActionsProvider, type CfoShellSelectOption } from './CfoPageShell'
import { CfoCoreFileMenu } from './CfoCoreFileMenu'
import { configureCfoCurrency, formatAsOf, trimStr } from './cfoFormat'
import { CFO_LIGHTWEIGHT_PAGE_IDS, CFO_ROUTE_FOCUS_METRIC_PARAM, cfoPageFromRoute } from './cfoRoutes'
import { cfoJobCostingAvailable, loadCfoBootstrapCached, useCfoBootstrap } from './useCfoBootstrap'
import { CfoMaterializationBanner, useCfoMaterialization } from './useCfoMaterialization'
import { DEFAULT_CFO_RUNTIME_PROFILE, isCfoMobileConstrained, useCfoRuntimeProfile, type CfoRuntimeProfile } from './cfoRuntimeProfile'

// CEO Analysis lazy-loads so DuckDB-WASM + the analysis runtime only ship to
// the browser after someone actually enters the page.
const CfoCeoAnalysisPage = lazy(() =>
  import('./ceo-analysis/CfoCeoAnalysisPage').then((m) => ({ default: m.CfoCeoAnalysisPage })),
)

// CfoUnifiedSurface — the in-shell router. This is the single app frame mounted
// on the `cfo-home` tab: it owns the active CFO section (`cfoActiveSection`) and
// renders either the Brief (home) or a native interior page INSIDE the unified
// shell, instead of swapping the workbook tab to the legacy `cfo-core` ribbon.
// Sections without a native calm page stay in-shell until a native surface
// exists; the classic ribbon is not a user-facing escape path.

export type CfoUnifiedSurfaceProps = {
  appApi: ServerApiClient
  workerClient?: AppWorkerClient | null
  workbookId: string | null
  apiSessionReady?: boolean
  /** Escape hatch to the untouched classic cfo-core tab. */
  onOpenCfoCore?: (target?: InsightDrillTarget | null) => void
  /** Open the Client Financials presentation tab. */
  onOpenCfoPresentation?: () => void
  /** Route the Explore page into the workbook's existing dashboard / viz /
   * spreadsheet surfaces (wired to the layout's tab switch). */
  onOpenSurface?: (surface: CfoExploreSurface) => void
  route?: CfoPageRoute | null
  onRouteChange?: (route: CfoPageRoute, historyMode?: 'push' | 'replace') => void
  navProfile?: CfoNavProfile
  classicEscapes?: boolean
  runtimeProfile?: CfoRuntimeProfile
  /** Provider-neutral workbook identity and effective capabilities. */
  accountingBootstrap?: Partial<AccountingWorkbookBootstrap> | null
}

type CfoGlobalComparisonOption = {
  id: MetricComparisonKind
  label: string
  shortLabel: string
  phrase: string
}

const CFO_GLOBAL_COMPARISON_OPTIONS: CfoGlobalComparisonOption[] = [
  { id: 'mom', label: 'vs. prior month', shortLabel: 'MoM', phrase: 'month-over-month' },
  { id: 'qoq', label: 'vs. prior quarter', shortLabel: 'QoQ', phrase: 'quarter-over-quarter' },
  { id: 'yoy', label: 'vs. prior year', shortLabel: 'YoY', phrase: 'year-over-year' },
  { id: 'l3m_vs_prior3m', label: 'last 3mo vs prior 3mo', shortLabel: 'Last 3mo', phrase: 'last 3 months vs the prior 3 months' },
  { id: 'l12m_vs_prior12m', label: 'last 12mo vs prior 12mo', shortLabel: 'Last 12mo', phrase: 'last 12 months vs the prior 12 months' },
]

const CFO_SECTION_QUERY_PARAM = 'fg_cfo_section'
const CFO_VIEW_QUERY_PARAM = 'fg_cfo_view'
const HOME_PERIOD_BOOTSTRAP_FIRST_PAINT_MS = 1500
const CFO_MOBILE_PAGE_IDS = new Set([
  'ceo',
  'home',
  'financials',
  'job_costing',
  'ratios',
  'cash',
  'ar',
  'forecast',
  'scenarios',
  'valuation',
  'qoe',
  'presentations',
  'gl_detail',
  'analysis_profile',
])

const cfoRouteFromLocation = (): CfoPageRoute => {
  if (typeof window === 'undefined') return { pageId: 'home', view: null }
  let params: URLSearchParams
  try {
    params = new URLSearchParams(window.location.search || '')
  } catch {
    return { pageId: 'home', view: null }
  }
  const section = trimStr(
    params.get(CFO_SECTION_QUERY_PARAM) ||
    params.get('cfo_section') ||
    params.get('cfoSection'),
  ).toLowerCase()
  const view = trimStr(params.get(CFO_VIEW_QUERY_PARAM) || params.get('cfo_view') || params.get('cfoView'))
  const route = section === 'advanced' && view === 'settings'
    ? { pageId: 'advanced' as const, view: 'settings' }
    : section
      ? resolveCfoPageFromNavId(section)
      : null
  return route ? { ...route, view: view || route.view } : { pageId: 'home', view: null }
}

const writeCfoRouteToLocation = (route: CfoPageRoute, historyMode: 'push' | 'replace' = 'push') => {
  if (typeof window === 'undefined') return
  try {
    const url = new URL(window.location.href)
    if (route.pageId === 'home') {
      url.searchParams.delete(CFO_SECTION_QUERY_PARAM)
      url.searchParams.delete(CFO_VIEW_QUERY_PARAM)
    } else {
      const section = route.pageId === 'advanced' && route.view === 'settings'
        ? 'settings'
        : route.pageId
      url.searchParams.set(CFO_SECTION_QUERY_PARAM, section)
      if (route.view && route.view !== route.pageId && route.view !== section) {
        url.searchParams.set(CFO_VIEW_QUERY_PARAM, route.view)
      } else {
        url.searchParams.delete(CFO_VIEW_QUERY_PARAM)
      }
    }
    if (route.pageId === 'ratios' && trimStr(route.focusMetricId)) {
      url.searchParams.set(CFO_ROUTE_FOCUS_METRIC_PARAM, trimStr(route.focusMetricId))
    } else {
      url.searchParams.delete(CFO_ROUTE_FOCUS_METRIC_PARAM)
    }
    const next = `${url.pathname}${url.search}${url.hash}`
    const current = `${window.location.pathname}${window.location.search}${window.location.hash}`
    if (next === current) return
    const fn = historyMode === 'replace' ? window.history.replaceState : window.history.pushState
    fn.call(window.history, window.history.state, '', next)
  } catch {
    // URL persistence is progressive; routing still works from React state.
  }
}

const normalizeCfoAsOfPeriod = (period: string | null | undefined): string => {
  const match = /^(\d{4})-(\d{2})/.exec(String(period || '').trim())
  return match ? `${match[1]}-${match[2]}-01` : ''
}

const cfoMonthStartIso = (year: number, monthIndex: number): string => {
  const date = new Date(year, monthIndex, 1)
  const yyyy = date.getFullYear()
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  return `${yyyy}-${mm}-01`
}

const cfoForecastAnchorPeriodOptions = (today = new Date()): CfoShellSelectOption[] => {
  const year = today.getFullYear()
  const monthIndex = today.getMonth()
  return [0, -1, -2].map((offset) => {
    const id = cfoMonthStartIso(year, monthIndex + offset)
    return { id, label: formatAsOf(id) }
  })
}

const mergeCfoPeriodOptions = (
  current: CfoShellSelectOption[],
  incoming: CfoShellSelectOption[],
): CfoShellSelectOption[] => {
  const byId = new Map<string, CfoShellSelectOption>()
  for (const option of current) byId.set(option.id, option)
  for (const option of incoming) byId.set(option.id, option)
  return [...byId.values()].sort((a, b) => b.id.localeCompare(a.id))
}

const cfoPeriodOptionsFromPeriods = (periods: Array<string | null | undefined>): CfoShellSelectOption[] => {
  const byId = new Map<string, CfoShellSelectOption>()
  for (const period of periods) {
    const normalized = normalizeCfoAsOfPeriod(period)
    if (normalized) byId.set(normalized, { id: normalized, label: formatAsOf(normalized) })
  }
  return [...byId.values()].sort((a, b) => b.id.localeCompare(a.id))
}

const bindingSavedAsOf = (binding: QuickBooksFpaWorkbookStatementBinding | null | undefined): string =>
  normalizeCfoAsOfPeriod(binding?.config_json?.asOfDate || binding?.config_json?.as_of_date)

const cfoBootstrapDefaultAsOf = (
  savedAsOf: string,
  defaultAsOf: string,
  options: CfoShellSelectOption[],
): string => {
  const ids = new Set(options.map((option) => option.id))
  const savedAvailable = savedAsOf && ids.has(savedAsOf)
  const defaultAvailable = defaultAsOf && ids.has(defaultAsOf)
  if (savedAvailable && (!defaultAvailable || savedAsOf <= defaultAsOf)) return savedAsOf
  if (defaultAvailable) return defaultAsOf
  if (savedAvailable) return savedAsOf
  return options[0]?.id || savedAsOf || defaultAsOf || ''
}

const cfoRouteAllowedForLightweight = (route: CfoPageRoute): boolean => {
  if (route.pageId === 'home') return true
  const pageSlug = cfoPageFromRoute(route)
  return pageSlug !== 'home' && CFO_LIGHTWEIGHT_PAGE_IDS.includes(pageSlug)
}

const cfoRouteAllowedForMobile = (route: CfoPageRoute): boolean => (
  CFO_MOBILE_PAGE_IDS.has(route.pageId) ||
  (route.pageId === 'advanced' && route.view === 'settings')
)

type CfoPeriodBindingRef = {
  workbookId: string
  binding: QuickBooksFpaWorkbookStatementBinding
}

type CfoPeriodSelectionSource = 'bootstrap' | 'user'

export function CfoUnifiedSurface({
  appApi,
  workerClient = null,
  workbookId,
  apiSessionReady = true,
  onOpenCfoPresentation,
  onOpenSurface,
  route: controlledRoute = null,
  onRouteChange,
  navProfile = 'full',
  runtimeProfile: providedRuntimeProfile,
  accountingBootstrap = null,
}: CfoUnifiedSurfaceProps) {
  const detectedRuntimeProfile = useCfoRuntimeProfile()
  const runtimeProfile = providedRuntimeProfile || detectedRuntimeProfile || DEFAULT_CFO_RUNTIME_PROFILE
  const mobileConstrained = isCfoMobileConstrained(runtimeProfile)
  const pageWorkerClient = mobileConstrained ? null : workerClient
  const [internalRoute, setInternalRoute] = useState<CfoPageRoute>(() => cfoRouteFromLocation())
  const [selectedEndPeriod, setSelectedEndPeriodRaw] = useState<string | null>(null)
  const [periodSelectionSource, setPeriodSelectionSource] = useState<CfoPeriodSelectionSource>('bootstrap')
  const [comparisonKind, setComparisonKind] = useState<MetricComparisonKind>('mom')
  const [periodOptions, setPeriodOptions] = useState<CfoShellSelectOption[]>([])
  const [periodBootstrapReady, setPeriodBootstrapReady] = useState(false)
  const [materializationNonce, setMaterializationNonce] = useState(0)
  const periodBindingRef = useRef<CfoPeriodBindingRef | null>(null)
  const periodPersistSeqRef = useRef(0)
  // Demo workbooks are immutable, prebuilt samples. Starting the live QuickBooks
  // materialization path for them can neither update the data nor recover a
  // failed run, and leaks that failure into the demo as a warning banner.
  // CFO Core has exactly one renderer for every provider. The accounting
  // bootstrap supplies identity/currency/capabilities; it must never select a
  // reduced provider-specific page implementation.
  const navigationBootstrapState = useCfoBootstrap(
    appApi,
    apiSessionReady && !accountingBootstrap ? workbookId : null,
  )
  const navigationBootstrap = accountingBootstrap || navigationBootstrapState.bootstrap
  const navigationBootstrapReady = Boolean(accountingBootstrap) || !navigationBootstrapState.bootstrapLoading
  const jobCostingAvailable = cfoJobCostingAvailable(navigationBootstrap)
  const qoeAvailable = true
  configureCfoCurrency(navigationBootstrap?.currency_code)
  const materializationEnabled = apiSessionReady && !isDemoSession()
  const materialization = useCfoMaterialization(appApi, workbookId, materializationEnabled, selectedEndPeriod)

  useEffect(() => {
    periodBindingRef.current = null
    setSelectedEndPeriodRaw(null)
    setPeriodSelectionSource('bootstrap')
    setPeriodOptions([])
    setPeriodBootstrapReady(!trimStr(workbookId))
  }, [workbookId])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const onComplete = (event: Event) => {
      const detail = (event as CustomEvent<{ workbookId?: string }>).detail
      if (trimStr(detail?.workbookId) !== trimStr(workbookId)) return
      setMaterializationNonce((value) => value + 1)
    }
    window.addEventListener('fg:cfo-materialization-complete', onComplete)
    return () => window.removeEventListener('fg:cfo-materialization-complete', onComplete)
  }, [workbookId])

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (controlledRoute) return
    const syncFromLocation = () => setInternalRoute(cfoRouteFromLocation())
    window.addEventListener('popstate', syncFromLocation)
    return () => window.removeEventListener('popstate', syncFromLocation)
  }, [controlledRoute])

  const route = controlledRoute || internalRoute
  const jobCostingRoutePending = route.pageId === 'job_costing' && !navigationBootstrapReady
  const availableNavIds = useMemo(
    () => new Set(cfoNavSectionsForProfile(navProfile, { jobCosting: jobCostingAvailable, qoe: qoeAvailable }).flatMap((section) => section.items.map((item) => item.id))),
    [jobCostingAvailable, navProfile, qoeAvailable],
  )
  const activeRoute = useMemo<CfoPageRoute>(() => {
    if (route.pageId === 'job_costing' && !jobCostingAvailable) {
      return { pageId: 'home', view: null }
    }
    if (mobileConstrained && !cfoRouteAllowedForMobile(route)) {
      return { pageId: 'home', view: null }
    }
    if (navProfile === 'lightweight' && !cfoRouteAllowedForLightweight(route)) {
      return { pageId: 'home', view: null }
    }
    return route
  }, [jobCostingAvailable, mobileConstrained, navProfile, route])
  const updateRoute = useCallback((next: CfoPageRoute, historyMode: 'push' | 'replace' = 'push') => {
    if (mobileConstrained && !cfoRouteAllowedForMobile(next)) {
      next = { pageId: 'home', view: null }
    }
    if (navProfile === 'lightweight' && !cfoRouteAllowedForLightweight(next)) return
    if (!controlledRoute) {
      setInternalRoute(next)
      writeCfoRouteToLocation(next, historyMode)
    }
    onRouteChange?.(next, historyMode)
  }, [controlledRoute, mobileConstrained, navProfile, onRouteChange])

  useEffect(() => {
    if (!navigationBootstrapReady || route.pageId !== 'job_costing' || jobCostingAvailable) return
    updateRoute({ pageId: 'home', view: null }, 'replace')
  }, [jobCostingAvailable, navigationBootstrapReady, route.pageId, updateRoute])

  const setSelectedEndPeriod = useCallback((value: string | null) => {
    const normalized = normalizeCfoAsOfPeriod(value)
    setPeriodSelectionSource('user')
    setSelectedEndPeriodRaw(normalized || null)
    if (normalized) {
      setPeriodOptions((current) => mergeCfoPeriodOptions(current, [{ id: normalized, label: formatAsOf(normalized) }]))
    }
    const periodBinding = periodBindingRef.current
    if (!normalized || !periodBinding) return

    const seq = periodPersistSeqRef.current + 1
    periodPersistSeqRef.current = seq
    const { workbookId: boundWorkbookId, binding } = periodBinding
    const nextConfig = {
      ...(binding.config_json && typeof binding.config_json === 'object' ? binding.config_json : {}),
      asOfDate: normalized,
      as_of_date: normalized,
    }
    const nextBinding: QuickBooksFpaWorkbookStatementBinding = {
      ...binding,
      config_json: nextConfig,
    }
    periodBindingRef.current = { workbookId: boundWorkbookId, binding: nextBinding }
    void appApi
      .updateQuickBooksFpaWorkbookStatementBinding(boundWorkbookId, binding.id, {
        connection_id: binding.connection_id,
        layout_version_id: binding.layout_version_id,
        config_json: nextConfig,
      })
      .then((updated) => {
        if (periodPersistSeqRef.current !== seq) return
        periodBindingRef.current = { workbookId: boundWorkbookId, binding: updated }
      })
      .catch(() => {
        if (periodPersistSeqRef.current !== seq) return
        periodBindingRef.current = periodBinding
      })
  }, [appApi])

  const registerPeriodOptions = useCallback((options: CfoShellSelectOption[]) => {
    if (!options.length) return
    setPeriodOptions((current) => mergeCfoPeriodOptions(current, options))
  }, [])

  const comparisonOptions = useMemo(
    () => CFO_GLOBAL_COMPARISON_OPTIONS.map((option) => ({ id: option.id, label: option.label })),
    [],
  )
  const forecastAnchorPeriodOptions = useMemo(() => cfoForecastAnchorPeriodOptions(), [])
  const forecastPeriodOptions = useMemo(
    () => mergeCfoPeriodOptions(periodOptions, forecastAnchorPeriodOptions),
    [forecastAnchorPeriodOptions, periodOptions],
  )

  useEffect(() => {
    let cancelled = false
    let homeFirstPaintHandle: number | null = null
    const wb = trimStr(workbookId)
    if (!wb) {
      setPeriodBootstrapReady(true)
      return
    }
    if (!apiSessionReady) {
      setPeriodBootstrapReady(false)
      return
    }
    if (activeRoute.pageId === 'ratios' || activeRoute.pageId === 'presentations') {
      setPeriodBootstrapReady(true)
      return
    }
    setPeriodBootstrapReady(false)
    if (activeRoute.pageId === 'home') {
      homeFirstPaintHandle = window.setTimeout(() => {
        if (!cancelled) setPeriodBootstrapReady(true)
      }, HOME_PERIOD_BOOTSTRAP_FIRST_PAINT_MS)
    }

    loadCfoBootstrapCached(appApi, wb)
      .then((bootstrap) => {
        if (cancelled || !bootstrap) return
        const binding = bootstrap.bindings?.[0] || null
        const bootstrapPeriodOptions = cfoPeriodOptionsFromPeriods(bootstrap.available_periods || [])
        if (bootstrapPeriodOptions.length) {
          setPeriodOptions((current) => mergeCfoPeriodOptions(current, bootstrapPeriodOptions))
        }
        if (binding) {
          periodBindingRef.current = { workbookId: wb, binding }
          const savedAsOf = bindingSavedAsOf(binding)
          const defaultAsOf = normalizeCfoAsOfPeriod(bootstrap.default_period)
          const nextAsOf = cfoBootstrapDefaultAsOf(savedAsOf, defaultAsOf, bootstrapPeriodOptions)
          if (nextAsOf) {
            setSelectedEndPeriodRaw((current) => current || nextAsOf)
            setPeriodSelectionSource((current) => current === 'user' ? current : 'bootstrap')
          }
        }
      })
      .catch(() => {
        // Page-level data fetches still publish the resolved as-of month.
      })
      .finally(() => {
        if (homeFirstPaintHandle != null) window.clearTimeout(homeFirstPaintHandle)
        if (!cancelled) setPeriodBootstrapReady(true)
      })

    return () => {
      cancelled = true
      if (homeFirstPaintHandle != null) window.clearTimeout(homeFirstPaintHandle)
    }
  }, [activeRoute.pageId, apiSessionReady, appApi, workbookId])

  // Rail selection: route to a native in-shell page when one exists.
  const handleNavSelect = useCallback(
    (navId: string) => {
      const next = resolveCfoPageFromNavId(navId)
      if (next && availableNavIds.has(navId)) {
        updateRoute(next)
        return
      }
    },
    [availableNavIds, updateRoute],
  )

  // Insight-card / journey drills: render in-shell when the drill target maps to
  // a native page. Unmapped classic targets are intentionally ignored.
  const handleOpenCfoCore = useCallback(
    (target?: InsightDrillTarget | null) => {
      const next = resolveCfoPageFromDrillTarget(target)
      if (next) {
        updateRoute(next)
      }
    },
    [updateRoute],
  )

  const openClassicFor = useCallback(
    (_navId: string) => () => {},
    [],
  )

  const workbookDownload = useCfoWorkbookDownload({ appApi, workbookId,
    company: ('company_name' in (navigationBootstrap || {}) ? (navigationBootstrap as { company_name?: string }).company_name : null)
      || (navigationBootstrap as Partial<AccountingWorkbookBootstrap> | null)?.accounting_company_name || 'Company',
    provider: (navigationBootstrap as Partial<AccountingWorkbookBootstrap> | null)?.accounting_provider || null,
    bootstrapLoading: !navigationBootstrapReady,
  })
  const shellActions = useMemo(
    () => (workbookId ? <><CfoWorkbookDownloadButton action={workbookDownload} /><CfoCoreFileMenu appApi={appApi} workbookId={workbookId} requestedPeriod={selectedEndPeriod} workbookDownload={workbookDownload} showSwitcher /></> : null),
    [appApi, selectedEndPeriod, workbookId, workbookDownload],
  )

  const renderRoute = () => {
  if (!apiSessionReady) {
    return <CfoPageLoading label="Loading CFO session..." />
  }
  if (jobCostingRoutePending) {
    return <CfoPageLoading label="Checking available reports..." />
  }
  if (activeRoute.pageId === 'ceo') {
    return (
      <AnalyticsProvider transport={appApi} distinctId={workbookId || null}>
        <CfoCeoCockpitPage
          appApi={appApi}
          workbookId={workbookId}
          onNavSelect={handleNavSelect}
          onOpenCfoCore={handleOpenCfoCore}
          navProfile={navProfile}
          runtimeProfile={runtimeProfile}
        />
      </AnalyticsProvider>
    )
  }
  if (activeRoute.pageId === 'ceo_analysis') {
    return (
      <AnalyticsProvider transport={appApi} distinctId={workbookId || null}>
        <Suspense fallback={<CfoPageLoading label="Loading CEO Analysis..." />}>
          <CfoCeoAnalysisPage
            appApi={appApi}
            workerClient={pageWorkerClient}
            workbookId={workbookId}
            onNavSelect={handleNavSelect}
            navProfile={navProfile}
            runtimeProfile={runtimeProfile}
          />
        </Suspense>
      </AnalyticsProvider>
    )
  }
  if (activeRoute.pageId === 'financials') {
    return (
      <AnalyticsProvider transport={appApi} distinctId={workbookId || null}>
        <CfoFinancialsPage
          appApi={appApi}
          workerClient={pageWorkerClient}
          workbookId={workbookId}
          selectedEndPeriod={selectedEndPeriod}
          onSelectedEndPeriodChange={setSelectedEndPeriod}
          periodOptions={periodOptions}
          onPeriodOptionsDiscovered={registerPeriodOptions}
          comparisonKind={comparisonKind}
          onComparisonKindChange={setComparisonKind}
          comparisonOptions={comparisonOptions}
          onNavSelect={handleNavSelect}
          onOpenClassic={openClassicFor('financials')}
          classicEscapes={false}
          navProfile={navProfile}
          runtimeProfile={runtimeProfile}
        />
      </AnalyticsProvider>
    )
  }
  if (activeRoute.pageId === 'qoe') {
    return (
      <AnalyticsProvider transport={appApi} distinctId={workbookId || null}>
        {/* `view` threads the route's density down: 'guided' (the default) or 'workspace'. */}
        <CfoQoePage appApi={appApi} workbookId={workbookId} view={activeRoute.view} onNavSelect={handleNavSelect} navProfile={navProfile} runtimeProfile={runtimeProfile} />
      </AnalyticsProvider>
    )
  }
  if (activeRoute.pageId === 'close') {
    return (
      <AnalyticsProvider transport={appApi} distinctId={workbookId || null}>
        <CfoClosePage
          appApi={appApi}
          workbookId={workbookId}
          onNavSelect={handleNavSelect}
          navProfile={navProfile}
          runtimeProfile={runtimeProfile}
        />
      </AnalyticsProvider>
    )
  }
  if (activeRoute.pageId === 'job_costing') {
    return <AnalyticsProvider transport={appApi} distinctId={workbookId || null}>
      <CfoJobCostingPage appApi={appApi} workbookId={workbookId} onNavSelect={handleNavSelect} navProfile={navProfile} runtimeProfile={runtimeProfile} />
    </AnalyticsProvider>
  }
  if (activeRoute.pageId === 'cash') {
    return (
      <AnalyticsProvider transport={appApi} distinctId={workbookId || null}>
        <CfoCashPage
          appApi={appApi}
          workerClient={pageWorkerClient}
          workbookId={workbookId}
          selectedEndPeriod={selectedEndPeriod}
          onSelectedEndPeriodChange={setSelectedEndPeriod}
          periodOptions={periodOptions}
          comparisonKind={comparisonKind}
          onComparisonKindChange={setComparisonKind}
          comparisonOptions={comparisonOptions}
          onNavSelect={handleNavSelect}
          onOpenClassic={openClassicFor('cash')}
          classicEscapes={false}
          navProfile={navProfile}
          runtimeProfile={runtimeProfile}
        />
      </AnalyticsProvider>
    )
  }
  if (activeRoute.pageId === 'ratios') {
    return (
      <AnalyticsProvider transport={appApi} distinctId={workbookId || null}>
        <CfoRatiosPage
          appApi={appApi}
          workerClient={pageWorkerClient}
          workbookId={workbookId}
          selectedEndPeriod={selectedEndPeriod}
          onSelectedEndPeriodChange={setSelectedEndPeriod}
          periodOptions={periodOptions}
          onPeriodOptionsDiscovered={registerPeriodOptions}
          comparisonKind={comparisonKind}
          onComparisonKindChange={setComparisonKind}
          comparisonOptions={comparisonOptions}
          onNavSelect={handleNavSelect}
          onOpenClassic={openClassicFor('ratios')}
          classicEscapes={false}
          navProfile={navProfile}
          runtimeProfile={runtimeProfile}
          focusMetricId={activeRoute.focusMetricId}
        />
      </AnalyticsProvider>
    )
  }
  if (activeRoute.pageId === 'ar') {
    return (
      <AnalyticsProvider transport={appApi} distinctId={workbookId || null}>
        <CfoArPage
          appApi={appApi}
          workerClient={pageWorkerClient}
          workbookId={workbookId}
          selectedEndPeriod={selectedEndPeriod}
          comparisonKind={comparisonKind}
          onSelectedEndPeriodChange={setSelectedEndPeriod}
          periodOptions={forecastPeriodOptions}
          onComparisonKindChange={setComparisonKind}
          comparisonOptions={comparisonOptions}
          onNavSelect={handleNavSelect}
          onOpenClassic={openClassicFor('ar')}
          classicEscapes={false}
          navProfile={navProfile}
          runtimeProfile={runtimeProfile}
        />
      </AnalyticsProvider>
    )
  }
  if (activeRoute.pageId === 'analysis_profile') {
    return (
      <AnalyticsProvider transport={appApi} distinctId={workbookId || null}>
        <CfoInteriorPage
          appApi={appApi}
          workerClient={pageWorkerClient}
          workbookId={workbookId}
          navId="analysis_profile"
          activeSection={CFO_SURFACES.advanced}
          eyebrow="AI"
          title="Context & Rules"
          lede="Review what FuseGrid knows about your business and how its AI should analyze and present results."
          crumb={<><b>AI</b> · Context & Rules</>}
          asOfLabel="—"
          copilotSubtitle="AI Tuning"
          copilotPlaceholder="Ask about a belief…"
          askPlaceholder="Ask about a belief…"
          onNavSelect={handleNavSelect}
          navProfile={navProfile}
          runtimeProfile={runtimeProfile}
        >
          {() => (
            accountingBootstrap?.accounting_provider && accountingBootstrap.accounting_provider !== 'quickbooks' ? (
              <div className="cfo-shell-notice" role="status">
                AI Tuning is unavailable for this provider because customer accounting data is excluded from model training and runtime generative commentary remains disabled pending legal approval. Deterministic forecasts and all governed financial reports remain available.
              </div>
            ) : (
              <CfoAnalysisProfilePage
                workbookId={workbookId ?? ''}
                loadUnderstanding={(id) =>
                  appApi.getQuickBooksUnderstanding(id).then((response) => ({
                    claims: response.claims.map(mapServerClaim),
                    forecastCashPlausible: response.forecast_cash_plausible ?? undefined,
                  }))
                }
                mutators={{
                  confirm: (claimId) => appApi.confirmUnderstandingClaim(workbookId ?? '', claimId),
                  reject: (claimId) => appApi.rejectUnderstandingClaim(workbookId ?? '', claimId),
                  redirect: (originId, instruction) =>
                    appApi.redirectUnderstanding(workbookId ?? '', originId, instruction),
                }}
                contextRulesClient={appApi}
              />
            )
          )}
        </CfoInteriorPage>
      </AnalyticsProvider>
    )
  }
  if (activeRoute.pageId === 'forecast') {
    const forecastSelectedEndPeriod = periodSelectionSource === 'bootstrap'
      ? null
      : selectedEndPeriod
    return (
      <AnalyticsProvider transport={appApi} distinctId={workbookId || null}>
        <CfoForecastPage
          appApi={appApi}
          workerClient={pageWorkerClient}
          workbookId={workbookId}
          selectedEndPeriod={forecastSelectedEndPeriod}
          comparisonKind={comparisonKind}
          onSelectedEndPeriodChange={setSelectedEndPeriod}
          periodOptions={forecastPeriodOptions}
          onComparisonKindChange={setComparisonKind}
          comparisonOptions={comparisonOptions}
          onNavSelect={handleNavSelect}
          onOpenClassic={openClassicFor('forecast')}
          classicEscapes={false}
          navProfile={navProfile}
          runtimeProfile={runtimeProfile}
        />
      </AnalyticsProvider>
    )
  }
  if (activeRoute.pageId === 'scenarios') {
    return (
      <AnalyticsProvider transport={appApi} distinctId={workbookId || null}>
        <CfoScenariosPage
          appApi={appApi}
          workerClient={pageWorkerClient}
          workbookId={workbookId}
          selectedEndPeriod={selectedEndPeriod}
          onSelectedEndPeriodChange={setSelectedEndPeriod}
          periodOptions={periodOptions}
          comparisonKind={comparisonKind}
          onComparisonKindChange={setComparisonKind}
          comparisonOptions={comparisonOptions}
          onNavSelect={handleNavSelect}
          onOpenClassic={openClassicFor('scenarios')}
          classicEscapes={false}
          navProfile={navProfile}
          runtimeProfile={runtimeProfile}
        />
      </AnalyticsProvider>
    )
  }
  if (activeRoute.pageId === 'valuation') {
    return (
      <AnalyticsProvider transport={appApi} distinctId={workbookId || null}>
        <CfoValuationPage
          appApi={appApi}
          workerClient={pageWorkerClient}
          workbookId={workbookId}
          selectedEndPeriod={selectedEndPeriod}
          onSelectedEndPeriodChange={setSelectedEndPeriod}
          periodOptions={periodOptions}
          comparisonKind={comparisonKind}
          onComparisonKindChange={setComparisonKind}
          comparisonOptions={comparisonOptions}
          onNavSelect={handleNavSelect}
          onOpenClassic={openClassicFor('valuation')}
          classicEscapes={false}
          navProfile={navProfile}
          runtimeProfile={runtimeProfile}
        />
      </AnalyticsProvider>
    )
  }
  if (activeRoute.pageId === 'presentations') {
    return (
      <AnalyticsProvider transport={appApi} distinctId={workbookId || null}>
        <CfoPresentationsPage
          appApi={appApi}
          workerClient={pageWorkerClient}
          workbookId={workbookId}
          onNavSelect={handleNavSelect}
          onOpenCfoCore={handleOpenCfoCore}
          onOpenClassic={() => onOpenCfoPresentation?.()}
          classicEscapes={false}
          navProfile={navProfile}
          runtimeProfile={runtimeProfile}
        />
      </AnalyticsProvider>
    )
  }
  if (activeRoute.pageId === 'explore') {
    return (
      <AnalyticsProvider transport={appApi} distinctId={workbookId || null}>
        <CfoExplorePage
          appApi={appApi}
          workerClient={pageWorkerClient}
          workbookId={workbookId}
          onNavSelect={handleNavSelect}
          onOpenSurface={onOpenSurface}
          navProfile={navProfile}
          runtimeProfile={runtimeProfile}
        />
      </AnalyticsProvider>
    )
  }
  if (activeRoute.pageId === 'gl_detail') {
    return (
      <AnalyticsProvider transport={appApi} distinctId={workbookId || null}>
        <CfoGlDetailExplorePage
          appApi={appApi}
          workerClient={pageWorkerClient}
          workbookId={workbookId}
          onNavSelect={handleNavSelect}
          navProfile={navProfile}
          runtimeProfile={runtimeProfile}
        />
      </AnalyticsProvider>
    )
  }
  if (activeRoute.pageId === 'advanced') {
    return (
      <AnalyticsProvider transport={appApi} distinctId={workbookId || null}>
        <CfoAdvancedPage
          appApi={appApi}
          workerClient={pageWorkerClient}
          workbookId={workbookId}
          activeView={activeRoute.view}
          onNavSelect={handleNavSelect}
          onOpenClassic={openClassicFor('gl_detail')}
          onOpenClassicTool={() => undefined}
          classicEscapes={false}
          navProfile={navProfile}
          runtimeProfile={runtimeProfile}
        />
      </AnalyticsProvider>
    )
  }

  if (activeRoute.pageId === 'home' && !periodBootstrapReady) {
    return <CfoPageLoading label="Loading CFO periods..." />
  }

  // Home (Brief). The Brief brings its own AnalyticsProvider.
  return (
    <CfoHomeBrief
      appApi={appApi}
      workerClient={pageWorkerClient}
      workbookId={workbookId}
      selectedEndPeriod={selectedEndPeriod}
      onSelectedEndPeriodChange={setSelectedEndPeriod}
      periodOptions={periodOptions}
      comparisonKind={comparisonKind}
      onComparisonKindChange={setComparisonKind}
      comparisonOptions={comparisonOptions}
      apiSessionReady={apiSessionReady}
      onOpenCfoCore={handleOpenCfoCore}
      onOpenCfoPresentation={onOpenCfoPresentation}
      onNavSelect={handleNavSelect}
      navProfile={navProfile}
      runtimeProfile={runtimeProfile}
    />
  )
  }

  // One analysis workspace for the whole surface: the modal stays mounted across
  // route changes while each page republishes its live context.
  return (
    <CfoNavAvailabilityProvider jobCosting={jobCostingAvailable} qoe={qoeAvailable}>
      <CfoShellActionsProvider actions={shellActions}>
        <CfoAnalysisProvider appApi={appApi} workerClient={pageWorkerClient} workbookId={workbookId}>
          <div style={{ display: 'flex', flex: '1 1 auto', flexDirection: 'column', height: '100%', minHeight: 0, overflow: 'hidden' }}>
            <CfoMaterializationBanner
              requestedPeriod={selectedEndPeriod}
              status={materialization.status}
              error={materialization.error}
              errorAttempts={materialization.errorAttempts}
              surfaceKey={activeRoute.pageId === 'cash' ? 'cash_planning' : undefined}
              onRetry={materialization.retryNow}
              appApi={appApi}
              workbookId={workbookId}
              bindingSetup={navigationBootstrapState.bootstrap?.binding_setup}
            />
            <div
              key={materializationNonce}
              style={{ display: 'flex', flex: '1 1 auto', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}
            >
              {renderRoute()}
            </div>
          </div>
        </CfoAnalysisProvider>
      </CfoShellActionsProvider>
    </CfoNavAvailabilityProvider>
  )
}

export default CfoUnifiedSurface
